import { useState } from 'react';
import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  PageHeader,
  TableCard,
  Tag,
  type Column,
} from '@sparkery/ui';
import type { PartnerRow } from '@sparkery/contracts';
import { useNavigate } from '@tanstack/react-router';
import { writeDevIdentity } from '../../lib/api.ts';
import { useAssumePartner } from '../../lib/partner-admin.ts';
import { shortDate, usePartners } from '../../lib/workspace.ts';
import { ratePercent } from '../../lib/report.tsx';
import { useViewer } from '../../lib/viewer.tsx';
import { NewPartnerForm } from './NewPartnerForm.tsx';
import './partners.css';

/**
 * Every partner company. Sparkery staff only.
 *
 * A row opens that partner's detail page, where their discount codes are mapped. Seeing
 * a partner's own screens is impersonation — a different, audited act that belongs to
 * the shell, not to a link in a table.
 */
export function PartnersView() {
  const [adding, setAdding] = useState(false);
  const partners = usePartners();
  const assume = useAssumePartner();
  const navigate = useNavigate();
  const { can, provider, viewer } = useViewer();
  const data = partners.data;
  const canImpersonate = viewer.audience === 'sparkery' && !viewer.impersonation;
  const canReport = can('reporting:read');

  function assumePartner(row: PartnerRow) {
    if (provider === 'dev') {
      writeDevIdentity({ user: 'admin', impersonate: row.partnerId });
      window.location.assign('/dashboard');
      return;
    }

    assume.mutate(row.partnerId, {
      onSuccess: () => window.location.assign('/dashboard'),
    });
  }

  // Archived partners stay in the list — their sales are part of the programme's
  // history and they may still have a final statement coming — but they sort last,
  // because the list is mostly used to find someone active.
  const rows = [...(data?.partners ?? [])].sort((a, b) => Number(a.archived) - Number(b.archived));
  const active = rows.filter((row) => !row.archived).length;

  const columns: Column<PartnerRow>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <span className="partnersName">
          <Avatar initials={row.initials} name={row.name} size="sm" />
          <strong>{row.name}</strong>
          {row.archived ? <Tag tone="neutral">Archived</Tag> : null}
        </span>
      ),
    },
    { id: 'email', header: 'Admin email', secondary: true, cell: (row) => row.adminEmail },
    {
      id: 'terms',
      header: 'Terms',
      // Three states, not two. A partner set to accrue with no rate is a
      // misconfiguration the commission domain refuses to guess at and throws on, so it
      // is named here rather than shown as a legitimate-looking "Fixed fee" — which is
      // what would hide it from the only people who can correct it.
      cell: (row) =>
        row.commissionAccrues && row.commissionRate ? (
          <Tag tone="confirmed">{ratePercent(row.commissionRate)}%</Tag>
        ) : row.commissionAccrues ? (
          <Tag tone="refunded">Rate missing</Tag>
        ) : (
          // Not "0%" — a fixed-fee partner earns outside the ledger, and a zero would
          // read as a partner earning nothing.
          <Tag tone="neutral">Fixed fee</Tag>
        ),
    },
    {
      id: 'links',
      header: 'Links / codes',
      numeric: true,
      secondary: true,
      cell: (row) => `${row.linkCount} / ${row.codeCount}`,
    },
    {
      id: 'signin',
      header: 'Sign-in',
      secondary: true,
      // Not the org id itself — it is long, opaque, and means nothing at a glance.
      // What an admin needs from the list is whether anybody can get in at all.
      cell: (row) =>
        row.workosOrgId ? (
          <Tag tone="confirmed">Mapped</Tag>
        ) : (
          <span className="partnersUnmapped">Not set up</span>
        ),
    },
    {
      id: 'created',
      header: 'Created',
      numeric: true,
      cell: (row) => shortDate(row.createdAt),
    },
  ];

  return (
    <>
      {/* Guarded on the permission, not the audience: a read-only staff role sees the
          list without being offered a form whose submit would 403. */}
      <PageHeader
        title="Partners"
        subtitle={
          data
            ? `${active} active${rows.length > active ? `, ${rows.length - active} archived` : ''}`
            : 'Manage partner companies'
        }
        actions={
          can('partners:manage') && !adding ? (
            <Button variant="ink" icon="plus" onClick={() => setAdding(true)}>
              New partner
            </Button>
          ) : null
        }
      />

      {partners.isError ? (
        <ErrorState
          title="Could not load partners"
          {...(partners.error instanceof Error ? { detail: partners.error.message } : {})}
          onRetry={() => void partners.refetch()}
        />
      ) : null}

      {assume.isError ? (
        <ErrorState
          title="Could not view as partner"
          {...(assume.error instanceof Error ? { detail: assume.error.message } : {})}
          onRetry={() => assume.reset()}
        />
      ) : null}

      {adding && can('partners:manage') ? (
        <NewPartnerForm onClose={() => setAdding(false)} />
      ) : null}

      <TableCard
        title="All partners"
        meta={
          data
            ? `${data.partners.length} ${data.partners.length === 1 ? 'partner' : 'partners'}`
            : undefined
        }
      >
        <DataTable
          caption="All partners"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.partnerId}
          loading={partners.isPending}
          onRowClick={(row) =>
            void navigate({
              to: '/admin/partners/$partnerId',
              params: { partnerId: row.partnerId },
            })
          }
          empty={<EmptyState title="No partners yet" body="Onboard a partner to see them here." />}
          rowAction={(row) => (
            <span className="partnersRowActions" onClick={(event) => event.stopPropagation()}>
              {canImpersonate ? (
                <IconButton
                  label={`View portal as ${row.name}`}
                  icon={<Icon name="eye" size={18} />}
                  disabled={assume.isPending}
                  onClick={() => assumePartner(row)}
                />
              ) : null}
              {canReport ? (
                <IconButton
                  label={`View report for ${row.name}`}
                  icon={<Icon name="reporting" size={18} />}
                  onClick={() =>
                    void navigate({
                      to: '/reporting',
                      search: { partnerId: row.partnerId },
                    })
                  }
                />
              ) : null}
            </span>
          )}
        />
      </TableCard>
    </>
  );
}
